/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useContext,
  useMemo,
  useRef,
  useEffect,
  createContext,
} from 'react';
import { css, jsx, keyframes } from '@emotion/core';
import { colors } from 'styles/variables';
import { interactionContext } from './interactionContext';
import getBlockById from 'services/apiHelpers/getBlockById';
import render from 'services/apiHelpers/renderBlock';
import EditableText from '../EditableText';
import EditableBtn from 'components/lib/EditableBtn';
import Modal from 'components/UI/Modal';
import { FeedbackWithEditableBtn } from 'components/lib/Feedback';
import { unlockInteraction } from 'services/unlockInteraction';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import isIE from 'services/isIE';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import getStore from 'services/getStore';
import EditWrapper from './EditWrapper';
import hotspotDefaultImage from 'assets/hotspotDefaultImage.png';
// import hotspotDefaultSVG from 'assets/HotspotDefault.svg';
import { useSelector } from 'react-redux';
import hotspotDefaultSVG from 'assets/HotspotDefault_v3.svg';
import hexToRGB from 'services/hexToRGB';
//
import Feedback from './Feedback';
import VisibleHotSpot from './VisibleHotSpot';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import SVG from '../SVG';

const { lPink, lGrey0, lGrey1, lGrey2 } = colors;

const HotSpot = ({ children, ...props }) => {
  let {
    id,
    image,
    src,
    hotspotType = 'circle',
    hotSpotColor,
    hotSpotSize,
    hotSpots,
    hotSpotsAnimated = true,
    hotSpotsVisible = true,
    useMediaInFeedback = true,
  } = props;

  // ' transform ' here to get image >>> src aligned
  // for legacy
  if (src) {
    image = src;
  }

  // image = 'http://orderindebris.com/cleaner_cols.png';
  // 'https://cdnb.artstation.com/p/assets/images/images/024/967/857/large/khyzyl-saleem-lambo-sci7web.jpg?1584118424';

  const context: any = useContext(interactionContext);

  const selected = getStore().getState().focusedNodeId === id;

  const [imgSize, setImgSize] = useState([960, 465]);
  const [imgLoading, setImgLoading] = useState(0);
  const [creating, setCreating] = useState(false);
  const [hilite, setHilite] = useState();
  const [showFeedback, setShowFeedback] = useState(null);
  const [{ scale, inverseScale }, setScale] = useState({
    scale: 1 || 0.3,
    inverseScale: 1 || 1 * (1 / 0.3),
  });

  const container = useRef(null);
  const root = useRef(null);
  const mounted = useRef(true);
  const currentHotspot = useRef(null);

  let { isOpen } = context;

  const interactionsOpen: any = useSelector(
    (state: any) => state.interactionsOpen
  );

  if (interactionsOpen.includes(context.id)) {
    isOpen = true;
  }

  const { r, g, b } = hexToRGB(hotSpotColor);

  const getParsedColor = opacity => {
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    return hotspotType === 'circle'
      ? `rgba(${r}, ${g}, ${b}, ${opacity})`
      : `rgba(255, 0, 99, ${opacity})`;
  };

  const keys = Object.keys(hotSpots);
  const values = Object.values(hotSpots);

  const itemsSeen = useRef(
    unlockInteraction(context.id, Object.keys(hotSpots).length)
  );

  const create = name => {
    // need to pull all of this out into the editor ?

    if (!name) {
      return setCreating(false);
    }

    currentHotspot.current = name;

    setCreating(true);
  };

  const doHilite = h => {
    // setHilite(h);
    // preffering to NOT set state for the hovers ?

    const hotSpotRoot = document.querySelector(`#hotSpot${id}`);
    if (!hotSpotRoot) return;

    const markers = Array.from(hotSpotRoot.querySelectorAll(hotspotType)); //  ? 'circle' : 'polygon'

    if (!markers.length) {
      return;
    }
    markers.forEach((m: any) => (m.style.fill = getParsedColor(0.5))); // 'rgba(255, 0, 99, 0.5)'

    if (!h) return;
    const marker: any = markers.find((m: any) => m.id === h);
    marker.style.fill = getParsedColor(0.8); // 'rgba(255, 0, 99, 0.8)'
  };

  useEffect(() => {
    mounted.current = true;
    return () => (mounted.current = false);
  }, []);

  useEffect(() => {
    if (window.dev()) return;

    if (context.props.lockDown === 0) return;
    showFeedback && itemsSeen.current(showFeedback);
  });

  useEffect(() => {
    if (!parent.hotspotEditorControls) return;

    const hotspotEditorControls = parent.hotspotEditorControls[id];
    if (hotspotEditorControls && hotspotEditorControls.getState)
      hotspotEditorControls.getState({
        creating,
      });
  });

  useEffect(() => {
    const onResize = () => {
      if (container.current) {
        const { width, height } = container.current.getBoundingClientRect();
        if (imgSize[0]) {
          let $ = width / imgSize[0];

          if (imgSize[0] <= width) {
            $ = 1;
          }

          mounted.current &&
            setScale({
              scale: $,
              inverseScale: 1 * (1 / $),
            });
        }
      }
    };

    // if (imgSize[0] > 960) {
    onResize();
    window.addEventListener('resize', onResize);
    // }

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, imgSize]);

  useEffect(() => {
    if (parent.hotspotEditorControls) {
      // if (!parent.hotspotEditorControls[id])
      parent.hotspotEditorControls[id] = {
        create,
        doHilite,
      };
    }

    // const img = new Image();
    // img.src = image;

    // img.onload = () => {
    //   setImgSize([img.width, img.height]);
    // };
    //
    // image,
  }, [hotspotType, hotSpotColor]);

  useEffect(() => {
    if (!image) {
      return;
    }

    // const isSVG = /\.(svg)$/i.test(image);
    if (/\.(svg)$/i.test(image)) {
      fetch(image)
        .then(r => r.text())
        .then(t => {
          const svgContainer: any = document.createElement('div') as any;
          svgContainer.innerHTML = t;

          const viewBox = svgContainer
            .querySelector('svg')
            .getAttribute('viewBox')
            .split(' ');
          const [x, y, width, height] = viewBox;

          setImgLoading(2);
          setImgSize([width, height]);
        });
    } else {
      const img = new Image();
      img.src = image;
      setImgLoading(1);

      img.onload = () => {
        // console.log('image size :', img.width, img.height);

        setImgLoading(2);
        setImgSize([img.width, img.height]);
      };
    }
  }, [isOpen, image]);

  // console.log({ scale, inverseScale, hotSpotSize });

  // ${window.dev() && `cursor: ${!selected ? 'pointer' : 'auto'};`}

  return !!imgSize.length ? (
    <div
      ref={container}
      css={css`
        position: relative;
        display: flex;
        justify-content: center;
        min-height: ${imgSize[1] * scale}px;

        ${isIE() &&
          `
          width: 100%;
          overflow: hidden;
    `}
      `}
    >
      <div
        css={css`
          position: relative;
          width: ${imgSize[0] * scale}px;
          height: ${imgSize[1] * scale}px;
          * {
            margin: 0;
            padding: 0;
          }
        `}
        // onClick={e => {
        //   // if(!window.dev()) return;
        //   if (!window.dev() || image || selected) return;
        //   setCurrentBlock({ id });
        // }}
      >
        <div
          ref={root}
          id={`hotSpot${id}`}
          css={css`
            position: absolute;
            left: 0;
            top: 0;
            width: ${imgSize[0]}px;
            height: ${imgSize[1]}px;
            transform: scale(${scale});
            transform-origin: 0 0;
            // background: #fff;
          `}
          onClick={e => {
            e.persist();

            if (creating)
              parent.hotspotEditorControls[id].getHotspots({
                ...(hotSpots && hotSpots),
                [currentHotspot.current]: [
                  ...(hotspotType === 'polygon' &&
                  hotSpots &&
                  hotSpots[currentHotspot.current]
                    ? hotSpots[currentHotspot.current]
                    : []),
                  (e.clientX - root.current.getBoundingClientRect().left) *
                    inverseScale,
                  (e.clientY - root.current.getBoundingClientRect().top) *
                    inverseScale,
                ],
              });
          }}
        >
          {[0, 2].includes(imgLoading) ? (
            <svg
              viewBox={`0 0 ${imgSize[0]} ${imgSize[1]}`}
              preserveAspectRatio="xMidYMid meet"
            >
              <image
                xlinkHref={image || hotspotDefaultSVG}
                width={imgSize[0]}
                height={imgSize[1]}
              />
              {(!window.dev() ? true : selected) &&
                !!keys.length &&
                values
                  .filter(
                    (h: any, i) =>
                      window.dev() ||
                      (children[i] && keys.includes(children[i].props.name))
                  )
                  .map((h: any, i) => (
                    <_ key={keys[i]}>
                      {hotspotType === 'circle' ? (
                        <VisibleHotSpot
                          id={keys[i]}
                          location={[h[0], h[1]]}
                          color={getParsedColor(0.8)}
                          hotSpotsAnimated={hotSpotsAnimated}
                          hotSpotsVisible={hotSpotsVisible}
                          size={hotSpotSize * inverseScale}
                          creating={creating}
                          onClick={() => {
                            !creating && setShowFeedback(keys[i]);
                          }}
                        />
                      ) : (
                        <polygon
                          // key={keys[i]}
                          id={keys[i]}
                          points={h.join(' ')}
                          css={css`
                            ${hotSpotsVisible &&
                              `&:hover {
                            fill: rgba(${r}, ${g}, ${b}, 0.25) !important;
                          }`}
                          `}
                          style={
                            window.dev()
                              ? {
                                  // fill: `rgba(255, 0, 99, ${
                                  //   hilite === keys[i] ? 0.8 : 0.5
                                  // })`,
                                  fill: `rgba(${r}, ${g}, ${b}, 0.5)`,
                                  stroke: hotSpotColor,
                                  strokeWidth: 1,
                                  cursor: !creating ? 'pointer' : 'auto',
                                  transition: 'fill 0.1s',
                                }
                              : {
                                  fill: 'rgba(0, 0, 0, 0)',
                                  ...(hotSpotsVisible && {
                                    stroke: hotSpotColor,
                                    strokeWidth: 1,
                                    cursor: 'pointer',
                                    transition: 'fill 0.1s',
                                  }),
                                }
                          }
                          onClick={e => {
                            !creating && setShowFeedback(keys[i]);
                          }}
                        />
                      )}
                    </_>
                  ))}
            </svg>
          ) : (
            <LoadingSpinner />
          )}
        </div>
      </div>
      {showFeedback && (
        <Feedback
          useMediaInFeedback={useMediaInFeedback}
          onClose={() => {
            // window.dev() && checkReturnToInteractionEditing(true);

            window.dev() &&
              requestAnimationFrame(() => {
                return;
                setCurrentBlock({ id });
              });

            setShowFeedback(null);
          }}
        >
          {
            children.filter(c => c.props.name === showFeedback)[0].props
              .children
          }
        </Feedback>
      )}
      {window.dev() && (!image || imgLoading === 2) && (
        <EditWrapper
          id={props.id}
          width={imgSize[0] * scale}
          height={imgSize[1] * scale}
          selected={selected}
        />
      )}
    </div>
  ) : null;
};

export default HotSpot;

// export default ({ children, ...props }) => {
//   // props.hotspotType
//   return (
//     <HotSpot key={props.image} {...props}>
//       {children}
//     </HotSpot>
//   );
// };
